// ===========================================
// Absence Notification API Functions
// ===========================================
// API calls for parent absence notification endpoints

import { apiClient } from '../services/api';
import { Student } from './users.api';

// ===========================================
// TYPES
// ===========================================

export type AbsenceReason =
  | 'ILLNESS'
  | 'SCHOOL_CAMP'
  | 'FAMILY_COMMITMENT'
  | 'DOCTOR_APPOINTMENT'
  | 'HOLIDAY'
  | 'OTHER';

export type UserRole = 'ADMIN' | 'TEACHER' | 'PARENT' | 'STUDENT';

export interface AbsenceNotification {
  id: string;
  schoolId: string;
  studentId: string;
  submittedById: string;
  submittedByRole: UserRole;
  startDate: string;
  endDate: string;
  reason: AbsenceReason;
  reasonDetails: string | null;
  isCancelled: boolean;
  cancelledAt: string | null;
  cancelledById: string | null;
  slotReleased: boolean;
  slotReleasedAt: string | null;
  createdAt: string;
  updatedAt: string;
  student: Student;
  submittedBy: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: UserRole;
  };
  cancelledBy: {
    id: string;
    firstName: string;
    lastName: string;
  } | null;
}

export interface AffectedLesson {
  id: string;
  name: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  lessonDate: string;
  teacher: {
    id: string;
    firstName: string;
    lastName: string;
  };
  room: {
    name: string;
    location: { name: string };
  };
  instrument: { name: string } | null;
}

/**
 * Per-row preview shown in the admin "Confirm Absence" modal.
 * `releasable` controls whether the row gets a release-toggle.
 */
export type NotReleasableReason =
  | 'GROUP_LESSON'
  | 'BAND_LESSON'
  | 'HYBRID_GROUP_WEEK'
  | 'HYBRID_BAND_WEEK'
  | 'HYBRID_NO_BOOKING'
  | 'GROUP_BAND_COMBINATION';

export interface AffectedSession {
  lessonId: string;
  lessonName: string;
  lessonDate: string;
  startTime: string;
  endTime: string;
  teacherName: string;
  lessonTypeLabel: string;
  releasable: boolean;
  notReleasableReason?: NotReleasableReason;
}

export interface CreateAbsenceInput {
  studentId: string;
  startDate: string; // ISO date string
  endDate: string; // ISO date string
  reason: AbsenceReason;
  reasonDetails?: string;
}

export interface AbsenceNotificationFilters {
  studentId?: string;
  startDate?: string;
  endDate?: string;
  includeCancelled?: boolean;
  page?: number;
  limit?: number;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface PaginatedAbsenceNotifications {
  data: AbsenceNotification[];
  pagination: Pagination;
}

export interface StudentAbsenceFilters {
  startDate?: string;
  endDate?: string;
  includeCancelled?: boolean;
}

// ===========================================
// HELPER FUNCTION
// ===========================================

/**
 * Transform backend response to frontend type
 * Maps Prisma relation names to frontend expectation
 */
function transformAbsenceNotification(data: any): AbsenceNotification {
  return {
    ...data,
    student: data.Student || data.student,
    submittedBy: data.User_AbsenceNotification_submittedByIdToUser || data.submittedBy,
    cancelledBy: data.User_AbsenceNotification_cancelledByIdToUser || data.cancelledBy,
  };
}

// ===========================================
// ABSENCE NOTIFICATION API
// ===========================================

export const absenceNotificationApi = {
  // ===========================================
  // PARENT OPERATIONS
  // ===========================================

  /**
   * Create a new absence notification
   */
  create: (data: CreateAbsenceInput): Promise<AbsenceNotification> =>
    apiClient
      .post<{ status: string; data: any }>('/absences', data)
      .then((res) => transformAbsenceNotification(res.data)),

  /**
   * Get affected lessons for a date range (preview before submitting)
   */
  getAffectedLessons: (
    studentId: string,
    startDate: string,
    endDate: string
  ): Promise<AffectedLesson[]> =>
    apiClient
      .get<{ status: string; data: AffectedLesson[] }>('/absences/affected-lessons', {
        params: { studentId, startDate, endDate },
      })
      .then((res) => res.data),

  /**
   * Get absence notifications for a specific student
   */
  getStudentAbsences: (
    studentId: string,
    filters?: StudentAbsenceFilters
  ): Promise<AbsenceNotification[]> =>
    apiClient
      .get<{ status: string; data: any[] }>(
        `/absences/student/${studentId}`,
        { params: filters }
      )
      .then((res) => res.data.map(transformAbsenceNotification)),

  /**
   * Get a single absence notification by ID
   */
  getById: (id: string): Promise<AbsenceNotification> =>
    apiClient
      .get<{ status: string; data: any }>(`/absences/${id}`)
      .then((res) => transformAbsenceNotification(res.data)),

  /**
   * Cancel an absence notification
   */
  cancel: (id: string): Promise<AbsenceNotification> =>
    apiClient
      .patch<{ status: string; data: any }>(`/absences/${id}/cancel`)
      .then((res) => transformAbsenceNotification(res.data)),

  // ===========================================
  // ADMIN OPERATIONS
  // ===========================================

  /**
   * Get all absence notifications (admin view) with pagination
   */
  getAll: (filters?: AbsenceNotificationFilters): Promise<PaginatedAbsenceNotifications> =>
    apiClient
      .get<{ status: string; data: any[]; pagination: Pagination }>('/absences', {
        params: filters,
      })
      .then((res) => ({
        data: res.data.map(transformAbsenceNotification),
        pagination: res.pagination,
      })),

  /**
   * Confirm an absence and (optionally) release the teacher's time slots for selected
   * 1:1 sessions. When `releaseSessions` is omitted, releases ALL releasable sessions
   * (backwards-compat). When it's an empty array, confirms only without releasing.
   * Each entry's `lessonDate` must be the ISO string returned by getAffectedSessions().
   * Admin only.
   */
  releaseSlot: (
    id: string,
    options?: {
      useMakeupCredit?: boolean;
      releaseSessions?: Array<{ lessonId: string; lessonDate: string }>;
    }
  ): Promise<{ slotsCreated: number; makeupCredit: { creditsGranted: number; creditsUsed: number } | null }> =>
    apiClient
      .post<{ status: string; data: { slotsCreated: number; makeupCredit: { creditsGranted: number; creditsUsed: number } | null } }>(
        `/absences/${id}/release-slot`,
        {
          useMakeupCredit: options?.useMakeupCredit ?? false,
          ...(options?.releaseSessions !== undefined
            ? { releaseSessions: options.releaseSessions }
            : {}),
        }
      )
      .then((res) => res.data),

  /**
   * Get every affected session in the absence window with a `releasable` flag,
   * powering the per-row toggles in the admin "Confirm Absence" modal.
   */
  getAffectedSessions: (id: string): Promise<AffectedSession[]> =>
    apiClient
      .get<{ status: string; data: AffectedSession[] }>(`/absences/${id}/affected-sessions`)
      .then((res) => res.data),

  /**
   * Get the student's current-term makeup credit balance (admin only)
   * Returns null if there is no active term.
   */
  getStudentCredits: (id: string): Promise<{
    termId: string;
    termName: string;
    creditsGranted: number;
    creditsUsed: number;
    creditsRemaining: number;
  } | null> =>
    apiClient
      .get<{ status: string; data: { termId: string; termName: string; creditsGranted: number; creditsUsed: number; creditsRemaining: number } | null }>(
        `/absences/${id}/student-credits`
      )
      .then((res) => res.data),
};

// ===========================================
// HELPER FUNCTIONS
// ===========================================

/**
 * Get display label for absence reason
 */
export const getAbsenceReasonLabel = (reason: AbsenceReason): string => {
  switch (reason) {
    case 'ILLNESS':
      return 'Illness';
    case 'SCHOOL_CAMP':
      return 'School Camp';
    case 'FAMILY_COMMITMENT':
      return 'Family Commitment';
    case 'DOCTOR_APPOINTMENT':
      return 'Doctor Appointment';
    case 'HOLIDAY':
      return 'Holiday';
    case 'OTHER':
      return 'Other';
    default:
      return reason;
  }
};

/**
 * Get color for absence reason chip
 */
export const getAbsenceReasonColor = (
  reason: AbsenceReason
): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' => {
  switch (reason) {
    case 'ILLNESS':
      return 'error';
    case 'SCHOOL_CAMP':
      return 'info';
    case 'FAMILY_COMMITMENT':
      return 'secondary';
    case 'DOCTOR_APPOINTMENT':
      return 'warning';
    case 'HOLIDAY':
      return 'success';
    case 'OTHER':
      return 'default';
    default:
      return 'default';
  }
};

/**
 * Format date range for display
 */
export const formatAbsenceDateRange = (startDate: string, endDate: string): string => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  const formatDate = (date: Date): string =>
    date.toLocaleDateString('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });

  if (start.toDateString() === end.toDateString()) {
    return formatDate(start);
  }

  return `${formatDate(start)} - ${formatDate(end)}`;
};

/**
 * Check if absence can be cancelled (is for future dates)
 */
export const canCancelAbsence = (absence: AbsenceNotification): boolean => {
  if (absence.isCancelled) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const endDate = new Date(absence.endDate);
  endDate.setHours(0, 0, 0, 0);

  // Can cancel if at least one day is in the future
  return endDate >= today;
};

/**
 * Check if an absence can be admin-confirmed (and slots optionally released):
 * - Not cancelled
 * - Not already confirmed
 * - At least one day is today or in the future
 */
export const canConfirmAbsence = (absence: AbsenceNotification): boolean => {
  if (absence.isCancelled) return false;
  if (absence.slotReleased) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const endDate = new Date(absence.endDate);
  endDate.setHours(0, 0, 0, 0);

  return endDate >= today;
};

/** @deprecated Use {@link canConfirmAbsence} — same predicate, clearer name. */
export const canReleaseSlot = canConfirmAbsence;

/**
 * Human-readable copy explaining why an affected session can't have its slot released.
 */
export const getNotReleasableLabel = (reason: NotReleasableReason): string => {
  switch (reason) {
    case 'GROUP_LESSON':
      return 'Group lesson — shared with other students';
    case 'BAND_LESSON':
      return 'Band rehearsal — shared with other students';
    case 'HYBRID_GROUP_WEEK':
      return 'Group week of a hybrid lesson';
    case 'HYBRID_BAND_WEEK':
      return 'Band week of a hybrid lesson';
    case 'HYBRID_NO_BOOKING':
      return 'Individual week with no confirmed booking';
    case 'GROUP_BAND_COMBINATION':
      return 'Hybrid group/band lesson — no individual slot';
    default:
      return 'No slot to release';
  }
};

/**
 * Absence reason options for dropdown
 */
export const ABSENCE_REASON_OPTIONS: Array<{ value: AbsenceReason; label: string }> = [
  { value: 'ILLNESS', label: 'Illness' },
  { value: 'SCHOOL_CAMP', label: 'School Camp' },
  { value: 'FAMILY_COMMITMENT', label: 'Family Commitment' },
  { value: 'DOCTOR_APPOINTMENT', label: 'Doctor Appointment' },
  { value: 'HOLIDAY', label: 'Holiday' },
  { value: 'OTHER', label: 'Other' },
];
